'use client';

import React, { useRef } from 'react';
import Image from 'next/image';

const steps = [
  {
    number: '01',
    title: 'Tell us about your accident',
    body: 'Call us or fill in the form. We need basic details — date, location, and the other driver\'s insurance info. Takes under 5 minutes.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
    image: '/assets/images/process_step_1_contact.png',
    alt: 'Person contacting No Limit Auto Body Specialist by phone to start a non-fault claim',
  },
  {
    number: '02',
    title: 'We handle the claim & collect your car',
    body: 'Our claims partner contacts the at-fault insurer directly. We arrange collection — often same or next day — and provide a courtesy car.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <rect x="1" y="3" width="15" height="13" rx="2"/>
        <path d="M16 8h4l3 3v5h-7V8z"/>
        <circle cx="5.5" cy="18.5" r="2.5"/>
        <circle cx="18.5" cy="18.5" r="2.5"/>
      </svg>
    ),
    image: '/assets/images/process_step_2_assess.png',
    alt: 'Mechanic assessing vehicle damage using a tablet with floating diagnostic data panel',
  },
  {
    number: '03',
    title: 'Your car is repaired & returned',
    body: 'We carry out all repairs to manufacturer standard — structural, paint, airbag, coding. Your car is returned fully repaired. You pay nothing.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    image: '/assets/images/process_step_3_repair.png',
    alt: 'Car being professionally repaired on a lift with courtesy car provided in the background',
  },
];

export default function ProcessSection() {
  const sectionRef  = useRef<HTMLElement>(null);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="relative z-10 bg-[#0D0D0D] border-b border-white/[0.06]"
      aria-label="How the no-cost claim process works"
    >
      <div className="max-w-8xl mx-auto px-6 py-24 md:py-36 relative">

        <div className="max-w-4xl mx-auto text-center mb-20 md:mb-28">
          <h2
            className="font-fraunces font-light text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-white"
            aria-label="If the accident wasn't your fault, you shouldn't pay a penny — so you won't."
          >
            If the accident wasn&apos;t your fault,
            you shouldn&apos;t pay a&nbsp;penny —&nbsp;so&nbsp;you&nbsp;won&apos;t.
          </h2>
          <div className="w-px h-14 bg-gradient-to-b from-[#CC1A1A]/60 to-transparent mx-auto mt-10" aria-hidden="true" />
        </div>

        {/* 3-step asymmetric cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-white/[0.07] rounded-3xl overflow-hidden">
          {steps?.map((step, i) => (
            <div
              key={i}
              className={`bento-card reveal-on-scroll hidden-init bg-[#161616] flex flex-col relative ${
                i < 2 ? 'border-b md:border-b-0 md:border-r border-white/[0.07]' : ''
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Step image */}
              <div className="relative w-full aspect-square overflow-hidden" style={{ aspectRatio: '1/1' }}>
                <Image
                  src={step?.image}
                  alt={step?.alt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#161616]/80" />
              </div>

              {/* Content */}
              <div className="p-8 md:p-10 lg:p-12 flex flex-col gap-6">
                {/* Step number */}
                <div className="flex items-start justify-between">
                  <span className="font-fraunces font-light italic text-[#CC1A1A] text-4xl leading-none">
                    {step?.number}
                  </span>
                  <div className="text-[#CC1A1A]/50" aria-hidden="true">
                    {step?.icon}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <h3 className="font-fraunces font-light text-xl md:text-2xl text-white leading-snug">
                    {step?.title}
                  </h3>
                  <p className="font-dm-sans text-[15px] text-white/50 leading-relaxed">
                    {step?.body}
                  </p>
                </div>
              </div>

              {/* Connector line (decorative) */}
              {i < 2 && (
                <div className="hidden md:block absolute right-0 top-1/2 w-px h-16 bg-gradient-to-b from-transparent via-[#CC1A1A]/30 to-transparent -translate-y-1/2" aria-hidden="true" />
              )}
            </div>
          ))}
        </div>

        {/* Reassurance strip */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 md:gap-12">
          {[
            'No excess to pay',
            'Courtesy car provided',
            'Manufacturer-standard repairs',
            'All paperwork handled',
          ]?.map((item) => (
            <div key={item} className="flex items-center gap-2.5 reveal-on-scroll hidden-init">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2.5" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span className="font-dm-sans text-[13px] font-500 text-white/40 tracking-wide">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}